import React, { useCallback, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '@/common/containers/Modals/ConfirmModal';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import {
  useQuestionListQuery,
  useSubmitAnswersMutation,
} from '@/entities/question';
import {
  useFinancialInstitutionQuestion,
  useFinancialInstitutionRows,
  useFinancialInstitutionSources,
  useFinancialInstitutionSubmit,
} from '@/modules/financials/hooks';
import {
  EMPTY_INSTITUTIONS,
  FINANCIAL_INSTITUTION_SLUG,
  findQuestionPathBySlug,
  getInstitutionDeleteConfirmTitle,
  getInstitutionDeleteDescription,
  getQuestionsFromQuestionData,
  shouldSkipInstitutionDeleteConfirmation,
} from '@/modules/financials/utils';
import styles from '@/modules/financials/components/FinancialInstitutionsModal.styles';

const FINANCIAL_INSTITUTIONS_FORM_ID = 'financial-institutions-form';

const FinancialInstitutionsModal = ({
  open,
  onClose,
  refreshTask,
  institutions: initialInstitutions = EMPTY_INSTITUTIONS,
}) => {
  const { t } = useTranslation();
  const decedentId = useCurrentDecedentId();
  const { data: mainQuestionsData } = useQuestionListQuery('main', decedentId);
  const submitAnswersMutation = useSubmitAnswersMutation();
  const loading = submitAnswersMutation.isPending;
  const institutionQuestion =
    useFinancialInstitutionQuestion(mainQuestionsData);
  const institutionQuestionPath = useMemo(
    () =>
      findQuestionPathBySlug(
        getQuestionsFromQuestionData(mainQuestionsData),
        FINANCIAL_INSTITUTION_SLUG
      ),
    [mainQuestionsData]
  );
  const parentQuestion =
    institutionQuestionPath?.[institutionQuestionPath.length - 2];
  const parentAnswer = useMemo(
    () =>
      (mainQuestionsData?.answers || []).find(
        (answer) =>
          answer.questionId === parentQuestion?.id && answer.status === 'active'
      ),
    [mainQuestionsData?.answers, parentQuestion?.id]
  );
  const { sourceInstitutions, isEdit } = useFinancialInstitutionSources(
    initialInstitutions,
    mainQuestionsData
  );
  const {
    institutions,
    archivedInstitutions,
    deletedInstitutionIds,
    setInstitutions,
    resetInstitutions,
    addInstitution,
    removeInstitution,
    updateInstitutionName,
    restoreInstitution,
  } = useFinancialInstitutionRows({ open, sourceInstitutions });

  const closeModal = useCallback(() => {
    if (loading) {
      return;
    }

    onClose?.();
    resetInstitutions();
  }, [loading, onClose, resetInstitutions]);

  const onSubmit = useFinancialInstitutionSubmit({
    closeModal,
    decedentId,
    deletedInstitutionIds,
    institutions,
    institutionQuestion,
    isEdit,
    parentAnswer,
    parentQuestion,
    refreshTask,
    setInstitutions,
    submitAnswersMutation,
    t,
  });

  const renderDeleteButton = (institution) => {
    const button = (onClick) => (
      <Button
        type="button"
        color="error"
        size="medium"
        startIcon={<DeleteIcon />}
        disabled={loading}
        onClick={onClick}
      >
        {t('Delete')}
      </Button>
    );

    if (shouldSkipInstitutionDeleteConfirmation(institution)) {
      return button(() => removeInstitution(institution.clientId));
    }

    return (
      <ConfirmModal
        type="delete"
        title={getInstitutionDeleteConfirmTitle(t, institution)}
        description={getInstitutionDeleteDescription(t, institution)}
        confirmText={t('delete')}
      >
        {(confirm) =>
          button(confirm(() => removeInstitution(institution.clientId)))
        }
      </ConfirmModal>
    );
  };

  return (
    <Dialog open={open} onClose={closeModal} fullWidth>
      <DialogTitle>{t('Financial Institutions')}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id={FINANCIAL_INSTITUTIONS_FORM_ID}
          onSubmit={onSubmit}
          sx={styles.item}
        >
          {institutions.map((institution, index) => (
            <React.Fragment key={institution.clientId}>
              <Box sx={styles.itemHeader}>
                <Typography variant="subtitle1">
                  {t('Financial institution {{index}}', {
                    index: index + 1,
                  })}
                </Typography>
                {renderDeleteButton(institution)}
              </Box>
              <TextField
                label={t('Name')}
                id={`financial-institution-${institution.clientId}`}
                autoFocus={index === 0}
                required
                value={institution.name}
                error={!!institution.error}
                helperText={institution.error}
                disabled={loading}
                sx={{ maxWidth: '320px', ml: 4 }}
                onChange={(event) =>
                  updateInstitutionName(
                    institution.clientId,
                    event.target.value
                  )
                }
              />
            </React.Fragment>
          ))}
          <Button
            type="button"
            color="success"
            size="medium"
            startIcon={<AddIcon />}
            disabled={loading}
            onClick={addInstitution}
            sx={{ width: 'fit-content' }}
          >
            {t('Add financial institution')}
          </Button>
          {archivedInstitutions.map((institution, index) => (
            <React.Fragment key={`${institution.clientId}-archived`}>
              <Box sx={styles.itemHeader}>
                <Typography variant="subtitle1">
                  {t('Archived {{item}}', {
                    item: t('Financial institution {{index}}', {
                      index: index + 1,
                    }),
                  })}
                </Typography>
                <Button
                  type="button"
                  color="primary"
                  size="medium"
                  startIcon={<RestoreIcon />}
                  disabled={loading}
                  onClick={() => restoreInstitution(institution.clientId)}
                >
                  {t('Restore')}
                </Button>
              </Box>
              <TextField
                label={t('Name')}
                id={`archived-financial-institution-${institution.clientId}`}
                value={institution.name}
                disabled
                sx={{ maxWidth: '320px', ml: 4 }}
              />
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          size="medium"
          onClick={closeModal}
          disabled={loading}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          form={FINANCIAL_INSTITUTIONS_FORM_ID}
          variant="contained"
          color="success"
          size="medium"
          loading={loading}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinancialInstitutionsModal;
